import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { GalleryAlbum } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Lightbox from "@/components/base/Lightbox";
import { mediaUrl } from "@/lib/media";

function formatDate(d?: string): string {
  if (!d) return "";
  return String(d).slice(0, 10).replace(/-/g, ".");
}

export default function GalleryDetailPage() {
  const { no } = useParams();
  const navigate = useNavigate();

  const { data, loading, error, reload } = useApi<GalleryAlbum>(
    no ? `gallery.php?no=${no}` : null
  );

  const [lightbox, setLightbox] = useState<number | null>(null);

  const album = data;
  const images = useMemo(() => {
    if (album?.images && album.images.length > 0) return album.images;
    const single = album?.photo || album?.thumb;
    return single ? [single] : [];
  }, [album]);
  const title = album?.title || album?.subject || "활동 사진";

  if (loading) {
    return (
      <>
        <PageBanner
          title="활동갤러리"
          breadcrumb={[{ label: "활동갤러리", to: "/gallery" }, { label: "사진 보기" }]}
        />
        <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </>
    );
  }

  if (error || !album) {
    return (
      <>
        <PageBanner
          title="활동갤러리"
          breadcrumb={[{ label: "활동갤러리", to: "/gallery" }, { label: "사진 보기" }]}
        />
        <section className="mx-auto max-w-4xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="사진을 불러오지 못했습니다"
            description={error ?? "존재하지 않는 게시물입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title="활동갤러리"
        breadcrumb={[{ label: "활동갤러리", to: "/gallery" }, { label: "사진 보기" }]}
      />

      <article className="mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-10">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          {title}
        </h1>
        <p className="mt-2 text-xs text-foreground-500">
          {formatDate(album.regdate || album.reg_date)}
          {typeof album.hit === "number" ? ` · 조회 ${album.hit}` : ""}
        </p>

        {album.content && (
          <div className="mt-5 whitespace-pre-wrap text-sm md:text-base leading-relaxed text-foreground-700">
            {album.content}
          </div>
        )}

        {images.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((img, i) => (
              <button
                key={`${img}-${i}`}
                type="button"
                onClick={() => setLightbox(i)}
                className="group overflow-hidden rounded-lg bg-background-100"
              >
                <div className="h-40 w-full overflow-hidden">
                  <img
                    src={mediaUrl(img)}
                    alt={`${title} ${i + 1}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex h-48 items-center justify-center rounded-xl border border-dashed border-background-300 text-sm text-foreground-400">
            등록된 사진이 없습니다
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/gallery")}
            className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </button>
        </div>
      </article>

      {lightbox !== null && (
        <Lightbox
          images={images}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
          alt={title}
        />
      )}
    </>
  );
}